import { motion, AnimatePresence } from 'framer-motion';
import { Gift, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TIER_CONFIG, TIER_TRANSITION_COPY, type TierType } from '@/types/engagement';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const copy = {
  en: {
    credits: '+{n} Announcement credits',
    discount: '{n}% Gift Store discount',
    legacy: 'Legacy Builder status unlocked',
    cta: 'Accept with gratitude',
  },
  es: {
    credits: '+{n} créditos de Anuncios',
    discount: '{n}% descuento en Tienda de Regalos',
    legacy: 'Estado de Constructor de Legado desbloqueado',
    cta: 'Aceptar con gratitud',
  },
  fr: {
    credits: '+{n} crédits d\'Annonces',
    discount: '{n}% de réduction Boutique Cadeaux',
    legacy: 'Statut Bâtisseur de Legacy débloqué',
    cta: 'Accepter avec gratitude',
  },
  pt: {
    credits: '+{n} créditos de Anúncios',
    discount: '{n}% desconto na Loja de Presentes',
    legacy: 'Status de Construtor de Legado desbloqueado',
    cta: 'Aceitar com gratidão',
  },
  ar: {
    credits: '+{n} رصيد إعلانات',
    discount: '{n}% خصم متجر الهدايا',
    legacy: 'تم فتح حالة باني الإرث',
    cta: 'قبول بامتنان',
  },
  ja: {
    credits: '+{n} アナウンスメントクレジット',
    discount: '{n}% ギフトストア割引',
    legacy: 'レガシービルダーステータス解放',
    cta: '感謝して受け取る',
  },
  ko: {
    credits: '+{n} 어나운스먼트 크레딧',
    discount: '{n}% 기프트 스토어 할인',
    legacy: '레거시 빌더 상태 잠금 해제',
    cta: '감사히 수락',
  },
  id: {
    credits: '+{n} kredit Pengumuman',
    discount: '{n}% diskon Toko Hadiah',
    legacy: 'Status Legacy Builder terbuka',
    cta: 'Terima dengan rasa syukur',
  },

  hi: {
    credits: '+{n} घोषणा क्रेडिट',
    discount: '{n}% गिफ्ट स्टोर छूट',
    legacy: 'Legacy Builder स्थिति अनलॉक की गई',
    cta: 'कृतज्ञता के साथ स्वीकार करें',
  },
};

interface RewardClaimCardProps {
  tierCompleted: TierType;
  credits?: number;
  discount?: number;
  legacyBuilder?: boolean;
  onClaim: () => void;
  onDismiss?: () => void;
  className?: string;
}

export function RewardClaimCard({
  tierCompleted,
  credits,
  discount,
  legacyBuilder,
  onClaim,
  onDismiss,
  className,
}: RewardClaimCardProps) {
  const tierConfig = TIER_CONFIG[tierCompleted];
  const tierCopyAll = TIER_TRANSITION_COPY[tierCompleted];
  const { language } = useLanguage();
  const t = lc(copy, language);
  const transitionCopy = tierCopyAll[language] || tierCopyAll.en;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <Card className={cn(
        'relative overflow-hidden border-2',
        'bg-gradient-to-br from-background via-background to-primary/5',
        'border-primary/30',
        className
      )}>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        )}

        <div className="absolute top-4 left-4 opacity-20">
          <Sparkles className="h-8 w-8 text-primary" />
        </div>

        <CardHeader className="text-center pb-2 pt-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="mx-auto mb-4 h-16 w-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${tierConfig.color}20` }}
          >
            <Gift className="h-8 w-8" style={{ color: tierConfig.color }} />
          </motion.div>
          <CardTitle className="text-xl">{transitionCopy.title}</CardTitle>
          <CardDescription className="text-base">
            {transitionCopy.message}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 pb-6">
          <div className="space-y-2 text-center">
            {credits && credits > 0 && (
              <motion.p
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="text-lg font-medium text-primary"
              >
                {t.credits.replace('{n}', String(credits))}
              </motion.p>
            )}
            {discount && discount > 0 && (
              <motion.p
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg font-medium text-secondary"
              >
                {t.discount.replace('{n}', String(discount))}
              </motion.p>
            )}
            {legacyBuilder && (
              <motion.p
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="text-lg font-medium text-accent"
              >
                {t.legacy}
              </motion.p>
            )}
          </div>

          <Button
            onClick={onClaim}
            size="lg"
            className="w-full gap-2"
          >
            <Gift className="h-4 w-4" />
            {t.cta}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
}
